import { Link } from "react-router-dom";
import { useState } from "react";
import { CircleNotch, EnvelopeSimple, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ email_configured: true });

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter your email");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/forgot-password", { email: email.trim() });
      setMeta(r.data);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6 sm:p-12" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          data-testid="back-to-login"
          className="inline-flex items-center gap-1 text-sm text-[#4A5568] hover:text-[#3B5BDB] smooth"
        >
          <ArrowLeft size={14} weight="bold" /> Back to sign in
        </Link>

        {submitted ? (
          <div className="mt-8 mc-card p-7" data-testid="forgot-success">
            <CheckCircle size={32} weight="duotone" className="text-[#16A34A]" />
            <h1 className="font-display text-3xl mt-4 text-[#0A1128]">Check your inbox</h1>
            <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">
              If <span className="font-medium text-[#0A1128]">{email}</span> matches a Mine AI account,
              we've emailed you a one-time link to reset your password. The link works for 1 hour.
            </p>
            {!meta.email_configured && (
              <p
                className="mt-4 text-xs text-[#8a6d1c] bg-[#D4AF37]/15 rounded p-3 leading-relaxed"
                data-testid="email-not-configured-warning"
              >
                Email delivery isn't fully configured on this server yet. If you don't receive an email
                within a few minutes, please contact your administrator to retrieve the reset link from
                the server logs.
              </p>
            )}
            <p className="mt-4 text-xs text-[#4A5568]">
              Didn't get it? Check your spam folder, or{" "}
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                className="text-[#3B5BDB] underline"
                data-testid="resend-trigger"
              >
                try again
              </button>
              .
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8" data-testid="forgot-form">
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={22} weight="duotone" className="text-[#3B5BDB]" />
              <h1 className="font-display text-3xl sm:text-4xl text-[#0A1128]">Forgot password?</h1>
            </div>
            <p className="mt-2 text-sm text-[#4A5568]">
              No worries — pop your email below and we'll send you a one-time link to reset it.
            </p>
            <div className="mt-7">
              <label className="text-xs uppercase tracking-wider text-[#4A5568]">Email</label>
              <input
                type="email"
                data-testid="forgot-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus
                required
                className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] smooth"
              />
            </div>
            <button
              type="submit"
              data-testid="forgot-submit"
              disabled={loading}
              className="mt-5 w-full h-11 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? <CircleNotch size={16} className="animate-spin" weight="bold" /> : null}
              {loading ? "Sending..." : "Send reset link"}
            </button>
            <p className="mt-4 text-xs text-[#4A5568]/80 text-center">
              Reset links expire after 1 hour and can only be used once.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
